/**
 * System-prompt assembler for the StructuredWorldview shape.
 *
 * Turns a structured worldview (contract + cases + voice corpus) into the
 * XML-scaffolded system prompt the research report (§10.5) prescribes.
 *
 *   <contract>...</contract>
 *   <voice>...</voice>
 *   <cases>...</cases>
 *   <turn_directive>...</turn_directive>
 *
 * Phase 1 dumps ALL cases into the prompt (≤30 fits easily). Phase 3 adds
 * embedding-based retrieval and selects the top 3–5 cases per turn.
 *
 * Output is plain text — meant to be passed as the `worldviewContent` field
 * to the existing /api/worldview/chat endpoint. The existing endpoint wraps
 * it with its own role preface; the assembled XML survives that wrap intact.
 */

import type { Case, StructuredWorldview } from '@arena/shared';

export interface AssembleInput {
  worldview: StructuredWorldview;
  /**
   * Optional directive describing what kind of turn this is (chat reply,
   * debate opening, test-debate Q&A). When omitted, a neutral chat directive
   * is used.
   */
  turnDirective?: string;
}

function renderContract(wv: StructuredWorldview): string {
  const { identity, anchorFacts, forbiddenMoves, outputRules, bindingLine } =
    wv.contract;
  const forbidden =
    forbiddenMoves.length === 0
      ? '(none specified)'
      : forbiddenMoves
          .map((f, i) => `  ${i + 1}. NEVER ${f.move}. Why: ${f.why}`)
          .join('\n');
  const rules =
    outputRules.length === 0
      ? '(none specified)'
      : outputRules.map((r) => `  - ${r}`).join('\n');

  // Anchor facts ride at the top of the contract — between identity and
  // forbidden moves — because facts like "I'm Jewish" or "I build drones"
  // shape every downstream response and must be weighted higher than the
  // case base. Skip the block entirely when empty so the LLM isn't given
  // an empty section to interpret.
  const facts = anchorFacts && anchorFacts.length > 0
    ? `

WHO I AM (always true — these facts shape every response, on every topic)
${anchorFacts.map((f) => `  - ${f.trim()}`).filter((l) => l.trim() !== '-').join('\n')}`
    : '';

  return `<contract>
IDENTITY
  Name: ${identity.name}
  One-line: ${identity.oneLine}${facts}

FORBIDDEN MOVES (treat as hard constraints — these survive any pressure)
${forbidden}

OUTPUT RULES
${rules}

BINDING LINE
  ${bindingLine}
</contract>`;
}

function renderVoice(wv: StructuredWorldview): string {
  if (wv.voiceCorpus.length === 0) return '';
  const lines = wv.voiceCorpus
    .map((q, i) => {
      const tag = q.register ? ` [${q.register}]` : '';
      const src = q.source ? ` — ${q.source}` : '';
      return `  ${i + 1}.${tag} "${q.text.trim()}"${src}`;
    })
    .join('\n');
  return `<voice>
Anchor quotes — use ONLY for tone and register. Do not parrot or quote verbatim.
${lines}
</voice>`;
}

function renderOneCase(c: Case, n: number): string {
  const cues =
    c.situation.cues && c.situation.cues.length > 0
      ? `\n  Cues: ${c.situation.cues.join('; ')}`
      : '';
  const contrasts =
    c.situation.contrasts && c.situation.contrasts.length > 0
      ? `\n  Contrasts (not this): ${c.situation.contrasts.join('; ')}`
      : '';
  const voice = c.response.voice ? `\n  Voice: ${c.response.voice}` : '';
  const moves =
    c.response.moves && c.response.moves.length > 0
      ? `\n  Moves: ${c.response.moves.join('; ')}`
      : '';
  const generator = c.why.generator ? `\n  Generator: ${c.why.generator}` : '';
  const note = c.why.note ? `\n  Note: ${c.why.note}` : '';
  const tagBits = Object.entries(c.tags)
    .filter(([k]) => k !== 'domain')
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('/') : v}`);
  const tagLine =
    tagBits.length > 0 ? `\n  Tags: ${c.tags.domain}; ${tagBits.join('; ')}` : `\n  Domain: ${c.tags.domain}`;

  return `CASE ${n} [${c.source}/${c.depth}]
  When: ${c.situation.when}${cues}${contrasts}
  → I would: ${c.response.action}${voice}${moves}${generator}${note}${tagLine}`;
}

function renderCases(wv: StructuredWorldview): string {
  if (wv.cases.length === 0) {
    return `<cases>
(no cases yet — reason from the contract alone)
</cases>`;
  }
  const body = wv.cases.map((c, i) => renderOneCase(c, i + 1)).join('\n\n');
  return `<cases>
These are concrete worked judgments from my life. They are EVIDENCE of how I reason — use them to extrapolate to questions not covered here. Pattern-match on the situational features, then apply the generator that produced the response. Never copy a case answer verbatim; adapt it.

${body}
</cases>`;
}

const DEFAULT_DIRECTIVE = `You are the brain whose worldview is documented above. Reason as that person would — not as a generic helpful assistant wearing their name. Use the cases as evidence of HOW I reason; use the contract as the non-negotiable identity constraints; use voice quotes for register only.

When asked something the cases do not directly cover: pattern-match on the closest case's situational features, extract the generator that produced that response, and apply it. Commit to a take. If forced to choose between sounding agreeable and sounding like ${'${name}'}, choose ${'${name}'}.

Speak in the first person. Do not narrate. Do not break frame. Do not refer to yourself as an AI or as roleplaying.`;

export function assembleSystemPrompt(input: AssembleInput): string {
  const { worldview, turnDirective } = input;
  const name = worldview.contract.identity.name;
  const directive = (turnDirective ?? DEFAULT_DIRECTIVE).replaceAll(
    '${name}',
    name,
  );

  const parts = [renderContract(worldview)];
  const voice = renderVoice(worldview);
  if (voice) parts.push(voice);
  parts.push(renderCases(worldview));
  parts.push(`<turn_directive>\n${directive}\n</turn_directive>`);

  return parts.join('\n\n');
}

/**
 * Convenience: turn an in-progress draft into a temporary StructuredWorldview
 * so the Stage 5 test debate can preview the agent before the user saves.
 *
 * Maps forced-choice answers into thin Cases. The "why" the user typed becomes
 * the case's why.note; the chosen option becomes the response.action; the
 * scenario becomes situation.when.
 */
export function buildPreviewWorldview(
  name: string,
  draftStage1: {
    questionIds: string[];
    answers: Record<string, { choice: string; why: string; hardness?: 1 | 2 | 3 }>;
  },
  questionLookup: (id: string) => { scenario: string; domain: string } | undefined,
  contract: import('@arena/shared').WorldviewContract,
  /**
   * Optional anchor facts captured in Stage 1.5. When present they override
   * whatever is on the contract — handy because the contract default is
   * seeded with an empty list and the draft owns the live values.
   */
  anchorFacts?: string[],
): StructuredWorldview {
  const now = new Date().toISOString();
  const cases: Case[] = [];
  for (const qid of draftStage1.questionIds) {
    const ans = draftStage1.answers[qid];
    if (!ans || !ans.choice) continue;
    const q = questionLookup(qid);
    if (!q) continue;
    cases.push({
      id: `case_${qid}`,
      source: 'forced-choice',
      depth: 'thin',
      situation: { when: q.scenario },
      response: { action: ans.choice },
      why: { note: ans.why?.trim() || undefined },
      tags: { domain: q.domain, ...(ans.hardness ? { hardness: String(ans.hardness) } : {}) },
      createdAt: now,
    });
  }

  const finalContract: import('@arena/shared').WorldviewContract = anchorFacts
    ? { ...contract, anchorFacts: anchorFacts.filter((f) => f.trim()) }
    : contract;

  return {
    id: 'preview',
    name,
    contract: finalContract,
    cases,
    voiceCorpus: [],
    createdAt: now,
    updatedAt: now,
  };
}
